import { ANIMATION_ICON } from '@/components/admin/experiences/page-editor/animation-picker';
import {
  BLOCK_TYPE_ICON,
  BlockTypeMenu,
} from '@/components/admin/experiences/page-editor/block-type-menu';
import {
  NOYAU_MODE_LABEL,
  PAGE_EDITOR_IDS,
  type PageSelection,
} from '@/components/admin/experiences/page-editor/contracts';
import { READER_BLOCK_LABEL } from '@/components/reader';
import { conditionPillText, namesThePerson } from '@/lib/display-conditions';
import { tempsDuplicateRefusal } from '@/lib/experience-duplicate';
import { HIDDEN_IN_THREAD_LABEL, TEMPS_IGNORED_LABEL } from '@/lib/experience-labels';
import {
  blockAnimationSummary,
  moveBlock,
  moveBlockBy,
  stackEntries,
  tempsSummary,
} from '@/lib/page-editor';
import { cn } from '@/lib/utils';
import type { VariableSources } from '@/lib/variable-pill';
import type {
  BlockType,
  ExperienceBlockDraft,
  ExperiencePageDraft,
} from '@matter_extractor/shared';
import { richTextPlain } from '@matter_extractor/shared';
import { Icon, Tooltip } from '@pms-digital/design-system';
import type { DragEvent as ReactDragEvent, KeyboardEvent as ReactKeyboardEvent } from 'react';
import { useState } from 'react';

/**
 * La pile des blocs, par temps (refonte modulaire §9.3; planche
 * `EditeurPageEcran`; issue #58).
 *
 * « Un séparateur de temps est une ligne avec un rond numéroté et le résumé du
 * temps ; un bloc est une ligne de 44 px avec sa poignée, l'icône de son type,
 * son libellé tronqué et, à droite, l'icône de son animation en gris. »
 *
 * ## Glisser NATIF, plus le clavier - et pas dnd-kit
 *
 * §9.3 named dnd-kit; the spec is corrected in this MR. The frise of the
 * protocol (§7.1) already reorders with the browser's own drag events and a
 * grip that answers the arrows, and it was written that way for a reason this
 * repository keeps stating: a dependency for a gesture the platform performs is
 * a runtime to ship, a sensor API to learn, and a second accessibility story.
 * So the rows are `draggable`, the SEPARATORS are drop targets - which is what
 * makes « y lâcher un bloc change son moment d'apparition » one gesture - and
 * the grip moves one step per arrow key.
 *
 * One step in the READING order and not inside the temps: the eye sees one
 * column of blocks, so an arrow that stopped at a temps boundary would refuse
 * to move a row the operator is looking at. `moveBlockBy` crosses.
 *
 * ## Le survol d'un séparateur est de l'état, la cible ne l'est pas
 *
 * A drop hint has to be drawn somewhere, and only the LIST knows where a drop
 * would land - a row knows it is being dragged and nothing more. Same shape as
 * `protocoles/step/objective-list.tsx`, and for the same reason.
 *
 * ## L'icône d'animation est un GESTE, et non plus un souvenir (issue #139)
 *
 * Elle était `aria-hidden`, doublée d'un `sr-only` que personne ne voyait, et
 * elle ne répondait à rien. Une icône seule est un hiéroglyphe : rien ne dit
 * que ce carré barré veut dire « aucune » ni que cette flèche veut dire
 * « glissé depuis la gauche ». Elle porte donc une bulle à DEUX lignes -
 * l'animation nommée avec la rapidité qu'elle hérite de son temps, puis le
 * geste - et un clic ouvre l'onglet Animation de son bloc. La seconde ligne
 * n'est pas de la politesse : sans elle, une icône muette n'aurait jamais
 * l'air cliquable, et le geste resterait à trouver.
 *
 * Elle reste en LECTURE SEULE, à l'inverse de la poignée et de l'icône de
 * copie qui disparaissent : ouvrir un inspecteur est une lecture, et c'est
 * même le seul chemin vers l'animation d'un bloc sur une version figée.
 *
 * ## Deux marques de plus, depuis l'issue #150 (§9.2)
 *
 * Un bloc CONDITIONNEL porte, sous son libellé, une pastille grise à entonnoir
 * par condition - « si @noyau · SGP » (maquette `EditeurConditions`) : un
 * marqueur de structure et pas une pastille de variable, d'où le gris et
 * l'entonnoir. La ligne grandit alors d'autant ; une ligne sans condition garde
 * ses 44 px. Et l'Input qui porte « Nom de la personne dans Sessions » (H75)
 * porte un petit disque bleu nuit à silhouette (maquette `InspecteurInputNom`),
 * le seul champ de l'expérience qui nomme la session.
 *
 * ## Une page jouée dans la conversation (prolongation.md §3.3, §6 ; issue #186)
 *
 * Dans le fil, la bulle de l'interviewer REMPLACE le Texte, l'Image et le Son, et
 * les temps sont ignorés. La pile ne les retire pas - ils servent si la case est
 * décochée : elle les marque, la ligne en POINTILLÉ avec « masqué dans le fil », et
 * le séparateur dit « ignoré dans le fil » à la place du délai. `conversation` est
 * un état de la page que le corps lit une fois et passe ; la pile n'en sait rien
 * de plus, ni de la règle de ce que le fil montre (`conversationItemsOf` de
 * `shared`, qu'elle ne rappelle pas : elle n'a besoin que de trois types).
 *
 * ## Deux gestes de plus dans les gouttières, depuis l'issue #136 (§9.3)
 *
 * Un « + » au bout du SÉPARATEUR ouvre le popover des neuf types et pose le
 * bloc neuf à la fin de CE temps : c'est ce qui remplace « à la fin du dernier
 * temps, puis glisse-le sur sept rangs ». Le séparateur devient pour ça un
 * `div role="button"` et non plus un `<button>` - un bouton ne peut pas en
 * contenir un autre, et c'est déjà la forme des lignes en dessous.
 *
 * Une CROIX rejoint la gouttière d'une ligne, après l'icône de copie, à la
 * place que §4.2 lui réservait - « juste avant la croix » se lisait jusqu'ici
 * dans un commentaire qui expliquait qu'il n'y en avait pas. « Supprimer ce
 * bloc » reste dans l'inspecteur : la croix est le geste rapide, le bouton est
 * celui qu'on trouve sans le connaître, et les deux sont annulables par la pile
 * de #135.
 */

/**
 * La seconde ligne de la bulle de l'icône d'animation (§9.3; issue #139).
 *
 * Le détail vient d'abord, le geste ensuite : on lit ce que la ligne ne dit
 * pas, puis on apprend ce qu'on peut en faire.
 */
const OPEN_ANIMATION = 'Ouvrir l’onglet Animation';

/**
 * « Texte · Avant de commencer » - what a row says about its block.
 *
 * Un Texte copiable dit son TEXTE, un Lien dit son LIBELLÉ et jamais son
 * adresse : une ligne de 44 px tronquée sur « https://… » ne distinguerait pas
 * deux liens du même domaine, là où deux libellés se lisent. Un bloc Noyau n'a
 * ni texte ni libellé : il dit son MODE, « Noyau · premier choix » (maquette
 * `EditeurPageNoyau`), qui est ce qui distingue ses trois lignes.
 */
export function blockRowLabel(block: ExperienceBlockDraft): string {
  const kind = READER_BLOCK_LABEL[block.type];
  const own = blockOwnLabel(block);
  return own === '' ? kind : `${kind} · ${own}`;
}

/**
 * The half of `blockRowLabel` after the type: what the block says of ITSELF,
 * and '' while it says nothing - an empty Texte, a Bouton without a label.
 *
 * The inspector's heading reads this and not the row label (issue #157): it
 * used to cut the row label up to a middle dot, which a bare type does not
 * carry, so an empty Texte was titled « Texte · Texte ».
 */
export function blockOwnLabel(block: ExperienceBlockDraft): string {
  return block.type === 'text'
    ? richTextPlain(block.text).replace(/\s+/g, ' ').trim()
    : block.type === 'image' || block.type === 'sound'
      ? (block.assetId ?? '')
      : block.type === 'copyable'
        ? block.text.replace(/\s+/g, ' ').trim()
        : block.type === 'noyau'
          ? NOYAU_MODE_LABEL[block.mode].toLowerCase()
          : block.type === 'selection' || block.type === 'note'
            ? block.question
            : block.label;
}

/** What a drag is carrying, and where it would land. */
type Drag = {
  readonly blockId: string;
  readonly overTempsId: string | null;
  readonly overBlockId: string | null;
  readonly after: boolean;
};

export interface BlockStackProps {
  readonly page: ExperiencePageDraft;
  readonly selection: PageSelection;
  readonly readOnly: boolean;
  readonly onSelect: (next: PageSelection) => void;
  readonly onPage: (next: ExperiencePageDraft) => void;
  /**
   * « Dupliquer ce bloc » (§4.2) - remonté au corps de l'éditeur, pas joué ici.
   *
   * Le suffixe du nom de variable se calcule contre la TIMELINE entière (§8.6)
   * et la pile ne tient qu'une page ; et la copie doit ouvrir son inspecteur,
   * dont l'onglet est un état du corps. Deux raisons, une seule conclusion :
   * la ligne appelle, le corps fait.
   */
  readonly onDuplicate: (blockId: string) => void;
  /**
   * « Ouvrir l'onglet Animation de ce bloc » (§9.3; issue #139).
   *
   * Remontée au corps comme `onDuplicate` juste au-dessus, et pour la même
   * raison : la sélection et l'onglet de l'inspecteur sont deux états du
   * corps, que la pile ne tient ni l'un ni l'autre. UN seul appel fait les
   * deux, ce qui est aussi pourquoi le clic arrête sa propagation - la ligne
   * sélectionnerait sinon le même bloc une seconde fois.
   */
  readonly onOpenAnimation: (blockId: string) => void;
  /**
   * « Ajouter un bloc à CE temps » - le « + » du séparateur (§9.3 ; issue #136).
   *
   * Remonté au corps pour la raison de `onDuplicate` : le bloc neuf prend la
   * sélection et ouvre son inspecteur sur Contenu, et ce sont deux états d'ici.
   * La pile nomme le temps, ce que la grille « Ajouter » ne savait pas faire.
   */
  readonly onAddBlock: (tempsId: string, type: BlockType) => void;
  /** La croix de la gouttière (§9.3 ; issue #136) - le corps déplace ensuite la sélection. */
  readonly onRemove: (blockId: string) => void;
  /** What names a condition's variable in its pill - the screen's scope (§8.6, §9.2). */
  readonly sources: VariableSources;
  /** The page is played in the conversation: its Texte, Image and Son are masked, its temps ignored. */
  readonly conversation?: boolean;
}

/** What the bubble of the interviewer replaces in the thread (prolongation.md §3.3). */
const HIDDEN_IN_THREAD: ReadonlySet<BlockType> = new Set<BlockType>(['text', 'image', 'sound']);

export function BlockStack({
  page,
  selection,
  readOnly,
  onSelect,
  onPage,
  onDuplicate,
  onOpenAnimation,
  onAddBlock,
  onRemove,
  sources,
  conversation = false,
}: BlockStackProps) {
  const [drag, setDrag] = useState<Drag | null>(null);
  const entries = stackEntries(page);

  function dropOnBlock(targetId: string): void {
    if (drag === null) return;
    const target = entries.find((entry) => entry.block.id === targetId);
    const moved = entries.find((entry) => entry.block.id === drag.blockId);
    setDrag(null);
    if (target === undefined || moved === undefined || target.block.id === moved.block.id) return;
    /*
      `index` is a rank in the temps WITHOUT the dragged block, which is what
      `moveBlock` takes: dropping below a row that sits under the dragged one in
      the SAME temps is the one case the two spellings disagree on.
    */
    let at = target.index + (drag.after ? 1 : 0);
    if (moved.tempsId === target.tempsId && moved.index < target.index) at -= 1;
    onPage(moveBlock(page, drag.blockId, { tempsId: target.tempsId, index: at }));
  }

  function dropOnTemps(tempsId: string): void {
    if (drag === null) return;
    setDrag(null);
    onPage(moveBlock(page, drag.blockId, { tempsId, index: 0 }));
  }

  function onGripKey(event: ReactKeyboardEvent<HTMLSpanElement>, blockId: string): void {
    if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return;
    event.preventDefault();
    event.stopPropagation();
    onPage(moveBlockBy(page, blockId, event.key === 'ArrowUp' ? -1 : 1));
  }

  return (
    <div data-testid={PAGE_EDITOR_IDS.stack} className="space-y-1">
      {page.temps.map((temps, tempsIndex) => {
        const chosen = selection.kind === 'temps' && selection.tempsId === temps.id;
        /*
          Le refus de duplication est celui du TEMPS (§4.2, §9.3), donc il se
          calcule UNE fois ici et non par ligne : un temps porte jusqu'à vingt
          blocs, et cette pile se redessine à chaque survol de glisser.
        */
        const refusal = tempsDuplicateRefusal(temps);
        return (
          <div key={temps.id} className="space-y-1">
            {/* biome-ignore lint/a11y/useSemanticElements: a <button> may not contain the « + » <button>. */}
            <div
              role="button"
              tabIndex={0}
              data-testid={PAGE_EDITOR_IDS.tempsSeparator}
              data-temps-id={temps.id}
              data-over={drag?.overTempsId === temps.id ? 'true' : 'false'}
              aria-pressed={chosen}
              onClick={() => onSelect({ kind: 'temps', tempsId: temps.id })}
              onKeyDown={(event) => {
                if (event.key !== 'Enter' && event.key !== ' ') return;
                event.preventDefault();
                onSelect({ kind: 'temps', tempsId: temps.id });
              }}
              onDragOver={(event: ReactDragEvent<HTMLElement>) => {
                if (drag === null) return;
                event.preventDefault();
                setDrag((was) =>
                  was === null
                    ? null
                    : { ...was, overTempsId: temps.id, overBlockId: null, after: false },
                );
              }}
              onDrop={(event: ReactDragEvent<HTMLElement>) => {
                event.preventDefault();
                dropOnTemps(temps.id);
              }}
              className={cn(
                'flex w-full cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-left transition-colors hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bleu-nuit',
                chosen ? 'bg-bleu-nuit-tint' : '',
                drag?.overTempsId === temps.id
                  ? 'shadow-[inset_0_0_0_2px_var(--color-bleu-nuit)]'
                  : '',
              )}
            >
              <span className="inline-flex size-5 shrink-0 items-center justify-center rounded-full bg-bleu-nuit font-semibold text-[10.5px] text-white tabular-nums">
                {tempsIndex + 1}
              </span>
              <span
                {...(conversation ? { 'data-testid': PAGE_EDITOR_IDS.tempsIgnoredMark } : {})}
                className="min-w-0 flex-1 truncate font-semibold text-bleu-nuit text-caption"
              >
                {conversation
                  ? `Temps ${tempsIndex + 1} · ${TEMPS_IGNORED_LABEL}`
                  : tempsSummary(temps, tempsIndex)}
              </span>

              {/*
                Le « + » de CE temps (§9.3 ; issue #136), au bout de la gouttière
                comme l'icône de copie d'une ligne. Au plafond du temps il est
                éteint et sa bulle porte le refus - le même `aria-disabled` que
                l'icône de copie, et pour la raison qu'elle énonce : un `span`
                ne peut pas être vraiment `disabled`, et une opacité pâlirait
                sans rendre inerte.
              */}
              {readOnly ? null : (
                <BlockTypeMenu
                  label={refusal ?? `Ajouter un bloc au temps ${tempsIndex + 1}`}
                  trigger="icon"
                  disabled={refusal !== null}
                  onPick={(type) => onAddBlock(temps.id, type)}
                />
              )}
            </div>

            <ul className="space-y-1">
              {temps.blocks.map((block) => {
                /*
                  Sur un Son, le `block` d'« aucune » et non l'icône de son
                  `animation` : le champ existe sur tous les blocs, le
                  mouvement non (§9.3). Lu dans la table plutôt qu'écrit ici,
                  pour que « les huit icônes sont les mêmes
                  partout » reste vrai d'une seule source.
                */
                const animationIcon =
                  block.type === 'sound' ? ANIMATION_ICON.none : ANIMATION_ICON[block.animation];
                const animationSummary = blockAnimationSummary(block, temps);
                const picked = selection.kind === 'block' && selection.blockId === block.id;
                const hidden = conversation && HIDDEN_IN_THREAD.has(block.type);
                const hint =
                  drag?.overBlockId === block.id ? (drag.after ? 'after' : 'before') : null;
                return (
                  <li key={block.id}>
                    <div
                      // biome-ignore lint/a11y/useSemanticElements: a <button> may not contain the grip <span tabindex>.
                      role="button"
                      tabIndex={0}
                      aria-pressed={picked}
                      data-testid={PAGE_EDITOR_IDS.blockRow}
                      data-block-id={block.id}
                      data-block-type={block.type}
                      data-hidden-in-thread={hidden ? 'true' : undefined}
                      draggable={!readOnly}
                      onClick={() => onSelect({ kind: 'block', blockId: block.id })}
                      onKeyDown={(event) => {
                        if (event.key !== 'Enter' && event.key !== ' ') return;
                        event.preventDefault();
                        onSelect({ kind: 'block', blockId: block.id });
                      }}
                      onDragStart={() =>
                        setDrag({
                          blockId: block.id,
                          overTempsId: null,
                          overBlockId: null,
                          after: false,
                        })
                      }
                      onDragOver={(event: ReactDragEvent<HTMLElement>) => {
                        if (drag === null) return;
                        event.preventDefault();
                        const box = event.currentTarget.getBoundingClientRect();
                        const after = event.clientY > box.top + box.height / 2;
                        setDrag((was) =>
                          was === null
                            ? null
                            : { ...was, overBlockId: block.id, overTempsId: null, after },
                        );
                      }}
                      onDrop={(event: ReactDragEvent<HTMLElement>) => {
                        event.preventDefault();
                        dropOnBlock(block.id);
                      }}
                      onDragEnd={() => setDrag(null)}
                      className={cn(
                        'flex min-h-11 cursor-pointer flex-wrap items-center gap-x-2 rounded-md border-2 bg-white px-2 transition-colors',
                        block.conditions.length > 0 ? 'gap-y-1 py-1.5' : '',
                        hidden ? 'border-dashed bg-gray-50' : '',
                        picked
                          ? 'border-bleu-nuit bg-bleu-nuit-tint'
                          : 'border-gray-200 hover:border-gray-400',
                        drag?.blockId === block.id ? 'opacity-40' : '',
                        hint === 'before'
                          ? 'shadow-[inset_0_3px_0_var(--color-bleu-nuit)]'
                          : hint === 'after'
                            ? 'shadow-[inset_0_-3px_0_var(--color-bleu-nuit)]'
                            : '',
                      )}
                    >
                      {readOnly ? null : (
                        <Tooltip content="Glisser pour réordonner, ou les flèches haut et bas">
                          {/* Focusable so the arrows reach it: the drag is on the row, and this
                              is the gesture's keyboard half (§9.3). */}
                          {/* biome-ignore lint/a11y/useSemanticElements: a <button> inside a role="button" row. */}
                          <span
                            role="button"
                            tabIndex={0}
                            data-testid={PAGE_EDITOR_IDS.blockGrip}
                            aria-label={`Déplacer ${blockRowLabel(block)} - flèches haut et bas`}
                            onKeyDown={(event) => onGripKey(event, block.id)}
                            onClick={(event) => event.stopPropagation()}
                            className="flex shrink-0 cursor-grab rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bleu-nuit"
                          >
                            <span className="inline-flex text-gray-300">
                              <Icon name="drag_indicator" size={16} />
                            </span>
                          </span>
                        </Tooltip>
                      )}
                      <span className="inline-flex shrink-0 text-gray-500">
                        <Icon name={BLOCK_TYPE_ICON[block.type]} size={16} />
                      </span>
                      <span
                        className={cn(
                          'min-w-0 flex-1 truncate text-small',
                          hidden ? 'text-gray-500' : 'text-gray-800',
                        )}
                      >
                        {blockRowLabel(block)}
                      </span>
                      {hidden ? (
                        <span
                          data-testid={PAGE_EDITOR_IDS.blockHiddenMark}
                          className="shrink-0 rounded-full border border-gray-200 bg-gray-100 px-1.5 text-[11px] text-gray-600 leading-4"
                        >
                          {HIDDEN_IN_THREAD_LABEL}
                        </span>
                      ) : null}
                      {namesThePerson(block) ? (
                        <Tooltip content="Nom de la personne dans Sessions">
                          <span
                            role="img"
                            data-testid={PAGE_EDITOR_IDS.personNameMark}
                            aria-label="Nom de la personne dans Sessions"
                            className="inline-flex size-5 shrink-0 items-center justify-center rounded-full bg-bleu-nuit text-white"
                          >
                            <Icon name="person" size={14} />
                          </span>
                        </Tooltip>
                      ) : null}
                      {/*
                        L'icône d'animation et sa bulle - voir l'en-tête.

                        Le NOM dit de QUOI il s'agit (« Animation de Texte ·
                        Avant de commencer »), la bulle dit son ÉTAT puis le
                        geste : les deux ne se recouvrent pas, donc la
                        description que le `Tooltip` pose sur le déclencheur
                        ajoute quelque chose au lieu de faire lire deux fois le
                        même mot. C'est le régime que ce composant documente -
                        « la description n'est posée que lorsqu'elle diffère du
                        nom accessible » -, et c'est l'autre moitié du même
                        régime qui fait passer à l'icône de copie le MÊME texte
                        aux deux quand elle ne refuse rien.

                        Un `span role="button"` et non un `<button>`, pour la
                        raison que la poignée et l'icône de copie donnent : la
                        ligne est elle-même un `role="button"`.
                        `stopPropagation` sur le clic, sinon le geste
                        sélectionnerait le bloc deux fois - une fois ici, une
                        fois par la ligne.

                        Sur un Son, le clic ouvre quand même l'onglet, qui
                        porte « Apparaît au temps » pour lui aussi ; c'est la
                        bulle qui dit pourquoi il n'y a pas d'animation à y
                        régler. Jusqu'ici un Son n'affichait aucune icône.
                      */}
                      <Tooltip
                        content={
                          <>
                            <span className="block">{animationSummary}</span>
                            <span className="block text-gray-600">{OPEN_ANIMATION}</span>
                          </>
                        }
                      >
                        {/* biome-ignore lint/a11y/useSemanticElements: a <button> inside a role="button" row. */}
                        <span
                          role="button"
                          tabIndex={0}
                          data-testid={PAGE_EDITOR_IDS.blockAnimation}
                          data-block-id={block.id}
                          data-animation={block.type === 'sound' ? 'none' : block.animation}
                          aria-label={`Animation de ${blockRowLabel(block)}`}
                          onClick={(event) => {
                            event.stopPropagation();
                            onOpenAnimation(block.id);
                          }}
                          onKeyDown={(event) => {
                            if (event.key !== 'Enter' && event.key !== ' ') return;
                            event.preventDefault();
                            event.stopPropagation();
                            onOpenAnimation(block.id);
                          }}
                          className="flex shrink-0 cursor-pointer rounded-sm text-gray-400 transition-colors hover:text-bleu-nuit focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bleu-nuit"
                        >
                          <Icon name={animationIcon} size={14} />
                        </span>
                      </Tooltip>

                      {/*
                        L'icône de copie, DANS LA GOUTTIÈRE de la ligne (§4.2).

                        « Juste avant la croix » partout ailleurs ; la ligne d'un
                        bloc n'en porte pas - « Supprimer ce bloc » vit dans
                        l'inspecteur depuis #58 - alors l'icône prend la place
                        que la croix prendrait, au bout de la gouttière. Absente
                        en lecture seule, comme la poignée et comme la croix.

                        Un `span role="button"` et non un `<button>`, pour la
                        raison que la poignée donne deux lignes plus haut : la
                        ligne est elle-même un `role="button"`. `stopPropagation`
                        sur le clic, sinon le geste sélectionnerait la SOURCE
                        avant que la copie ne prenne la sélection.

                        Au plafond, `aria-disabled` plutôt qu'un `disabled` sous
                        `TooltipShield` : la forme que l'enveloppe attend est un
                        contrôle VRAIMENT `disabled`, et celui-ci ne peut pas
                        l'être - c'est un `span`, comme la poignée, parce que la
                        ligne est déjà un `role="button"`. L'attribut le dit donc
                        à la synthèse vocale, le `span` reste survolable pour que
                        la bulle porte la raison (§4.2), et ce sont les deux
                        gestionnaires qui refusent. L'encre éteinte est
                        `text-gray-300`, celle des croix fermées de l'éditeur de
                        critère, et jamais une opacité - une opacité pâlit sans
                        rendre inerte, et c'est la règle que `button.tsx` énonce.
                      */}
                      {readOnly ? null : (
                        <Tooltip content={refusal ?? `Dupliquer ${blockRowLabel(block)}`}>
                          {/* biome-ignore lint/a11y/useSemanticElements: a <button> inside a role="button" row. */}
                          <span
                            role="button"
                            tabIndex={0}
                            data-testid={PAGE_EDITOR_IDS.blockDuplicate}
                            data-block-id={block.id}
                            aria-label={`Dupliquer ${blockRowLabel(block)}`}
                            aria-disabled={refusal !== null}
                            onClick={(event) => {
                              event.stopPropagation();
                              if (refusal !== null) return;
                              onDuplicate(block.id);
                            }}
                            onKeyDown={(event) => {
                              if (event.key !== 'Enter' && event.key !== ' ') return;
                              event.preventDefault();
                              event.stopPropagation();
                              if (refusal !== null) return;
                              onDuplicate(block.id);
                            }}
                            className={cn(
                              'flex shrink-0 rounded-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bleu-nuit',
                              refusal === null
                                ? 'cursor-pointer text-gray-400 hover:text-bleu-nuit'
                                : 'cursor-not-allowed text-gray-300',
                            )}
                          >
                            <Icon name="content_copy" size={14} />
                          </span>
                        </Tooltip>
                      )}

                      {/*
                        La croix, APRÈS l'icône de copie (§9.3 ; issue #136).

                        « Juste avant la croix » : la gouttière de tout le reste
                        de cette application range la copie puis la croix, et
                        c'est cet ordre-là que la ligne prend enfin. Absente en
                        lecture seule comme les deux autres ; jamais éteinte -
                        une suppression n'a pas de plafond -, et annulable par
                        la pile de #135, ce qui est ce qui permet de la mettre
                        là plutôt que derrière une confirmation.

                        Un `span role="button"`, `stopPropagation` sur le clic :
                        les raisons sont celles de l'icône de copie juste
                        au-dessus, mot pour mot.
                      */}
                      {readOnly ? null : (
                        <Tooltip content={`Supprimer ${blockRowLabel(block)}`}>
                          {/* biome-ignore lint/a11y/useSemanticElements: a <button> inside a role="button" row. */}
                          <span
                            role="button"
                            tabIndex={0}
                            data-testid={PAGE_EDITOR_IDS.blockRemoveRow}
                            data-block-id={block.id}
                            aria-label={`Supprimer ${blockRowLabel(block)}`}
                            onClick={(event) => {
                              event.stopPropagation();
                              onRemove(block.id);
                            }}
                            onKeyDown={(event) => {
                              if (event.key !== 'Enter' && event.key !== ' ') return;
                              event.preventDefault();
                              event.stopPropagation();
                              onRemove(block.id);
                            }}
                            className="flex shrink-0 cursor-pointer rounded-sm text-gray-400 transition-colors hover:text-rouge-deep focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bleu-nuit"
                          >
                            <Icon name="close" size={14} />
                          </span>
                        </Tooltip>
                      )}

                      {/* Les pastilles de condition, sur une ligne à elles sous le
                          libellé (§9.2) : `basis-full` les renvoie à la ligne, et
                          le retrait les aligne sous le libellé, pas sous la poignée. */}
                      {block.conditions.length === 0 ? null : (
                        <span
                          className={cn(
                            'flex basis-full flex-wrap gap-1',
                            readOnly ? 'pl-6' : 'pl-12',
                          )}
                        >
                          {block.conditions.map((condition, index) => (
                            <span
                              // biome-ignore lint/suspicious/noArrayIndexKey: a condition's rank is its only address.
                              key={index}
                              data-testid={PAGE_EDITOR_IDS.blockConditionPill}
                              className="inline-flex min-w-0 max-w-full items-center gap-1 rounded-full bg-gray-100 px-2 font-semibold text-[11px] text-gray-700 leading-5"
                            >
                              <span className="inline-flex shrink-0">
                                <Icon name="filter_list" size={14} />
                              </span>
                              <span className="min-w-0 truncate">
                                {conditionPillText(condition, sources)}
                              </span>
                            </span>
                          ))}
                        </span>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        );
      })}
    </div>
  );
}
